import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import {
    Aderente, DocumentoPratica, NotaPratica, ODataList, PraticaAderente, RiepilogoAderente, StatoPratica,
    TipologiaPrestazione, VoceDizionario,
} from './models';

/** API tipizzate dell'area aderente. I dizionari si caricano una volta e si condividono. */
@Injectable({ providedIn: 'root' })
export class AderenteApiService {
    private http = inject(HttpClient);
    private base = '/api/aderente';

    readonly me$ = this.http.get<Aderente>(`${this.base}/me`).pipe(shareReplay(1));
    readonly statiPratica$ = this.dizionario<StatoPratica>('/api/dizionari/stati-pratica');
    readonly tipologiePratica$ = this.dizionario<VoceDizionario>('/api/dizionari/tipologie-pratica');
    readonly tipiDocumento$ = this.dizionario<VoceDizionario>('/api/dizionari/tipi-documento');
    readonly tipologiePrestazione$ = this.dizionario<TipologiaPrestazione>('/api/lookup/tipologie-prestazione');

    pratiche(): Observable<PraticaAderente[]> {
        return this.http.get<ODataList<PraticaAderente>>(`${this.base}/pratiche`, { params: { '$orderby': 'dataInserimento desc' } })
            .pipe(map(r => r.value));
    }

    pratica(aid: string): Observable<PraticaAderente> {
        return this.http.get<PraticaAderente>(`${this.base}/pratiche/${aid}`);
    }

    riepilogo(): Observable<RiepilogoAderente> {
        return this.http.get<RiepilogoAderente>(`${this.base}/riepilogo`);
    }

    documenti(aid: string): Observable<DocumentoPratica[]> {
        return this.http.get<ODataList<DocumentoPratica>>(`${this.base}/pratiche/${aid}/documenti`, { params: { '$orderby': 'caricatoIl asc' } })
            .pipe(map(r => r.value));
    }

    /** Contenuto del documento (GET …/documenti/{docAid}/file). */
    scaricaDocumento(aid: string, docAid: string): Observable<Blob> {
        return this.http.get(`${this.base}/pratiche/${aid}/documenti/${docAid}/file`, { responseType: 'blob' });
    }

    caricaDocumenti(aid: string, tipo: string, files: File[]): Observable<unknown> {
        const body = new FormData();
        body.append('tipo', tipo);
        files.forEach(f => body.append('file', f, f.name));
        return this.http.post(`${this.base}/pratiche/${aid}/documenti`, body);
    }

    note(aid: string): Observable<NotaPratica[]> {
        return this.http.get<ODataList<NotaPratica>>(`${this.base}/pratiche/${aid}/note`, { params: { '$orderby': 'createdAt asc' } })
            .pipe(map(r => r.value));
    }

    inviaNota(aid: string, oggetto: string, testo: string): Observable<NotaPratica> {
        return this.http.post<NotaPratica>(`${this.base}/pratiche/${aid}/note`, { oggetto, testo });
    }

    private dizionario<T>(url: string): Observable<T[]> {
        return this.http.get<ODataList<T>>(url).pipe(map(r => r.value), shareReplay(1));
    }
}
